import * as authActions from '~/actions/auth.actions';
import { ActionKeys } from '~/actions/constants';
import { addEventProperties, sendTrackEvent } from '~/actions/prod-analytics.actions';
import { AxiosResponse } from 'axios';
import { MarketoForms2 } from '~/components/Marketo';
import { push } from 'connected-react-router';
import { USER_REGISTER_SUCCESS_COOKIE_NAME } from '~/constants/cookies';
import { ADDITIONAL_INFORMATION_GTM_RESULTS } from '~/constants/local-storage-keys';
import * as Cookies from 'js-cookie';
import { getEmbeddableUser } from '~/pages/common/EmbeddableAuth';
import { OnboardingLocalStorageKeys } from '~/pages/Onboarding/onboarding.constants';
import { stopSubmit } from 'redux-form';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { getTemporaryToken } from '~/selectors/auth.selectors';
import { getParsedQS } from '~/selectors/router.selectors';
import * as authService from '~/services/auth.service';
import { formatGTMAdditionalInformation, GTMAdditionalInformation } from '~/types/GTM';
import * as RegisterUser from '~/types/RegisterUser';
import { isSupportedStore } from '~/types/Store';
import { getCookie } from '~/utils/cookies';
import { saveInLocalStorage } from '~/utils/local-storage';
import { getLocale } from '~/utils/locale';
import { setAttributionCookie } from '~/utils/marketing-source-attribution';
import { extractMarketingPayloadFromCookie } from '~/utils/utm-tracking';
import { selectState } from '..';
import { updateOnboardingInfo } from '~/pages/Onboarding/store/onboarding.actions';
import { OnboardingInfoFormDataWithValidation } from '~/pages/Onboarding/info/OnboardingInfo';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    MktoForms2: MarketoForms2; // No specific type
  }
}

const PLAYWRIGHT_TESTER_EMAIL = 'stester+playwright';

function preventMarketoTracking({ user }: RegisterUser.RegisterUser) {
  return (
    CONFIG.environmentName !== 'production' || // Marketo form submit only for production
    user.email.startsWith(PLAYWRIGHT_TESTER_EMAIL) // prevent test users
  );
}

export function* register(action: ReturnType<typeof authActions.register>) {
  try {
    let UTMCookies = Cookies.getJSON('shippoUTM');

    // This cookie is set in GTM, if for some reason GTM is not loaded and the cookie is undefined
    // we try to set it again calling the source code instead of waiting on GTM.
    // Then, we get the cookie again
    if (!UTMCookies) {
      setAttributionCookie();
      UTMCookies = Cookies.getJSON('shippoUTM');
    }

    UTMCookies = UTMCookies || {};

    const QS: Record<string, string> = yield select(getParsedQS);
    const pricePlan = getStartingPricePlan(QS);
    const marketingPayload = extractMarketingPayloadFromCookie(UTMCookies);
    const embeddableUser = getEmbeddableUser();

    const trackerSessionId = Cookies.get('tracker_sessionid') || '';

    const trackingPayload = {
      tracker_sessionid: trackerSessionId,
    };

    const newPayload: RegisterUser.RegisterUser = {
      ...action.payload,
      marketing: {
        regMethod: embeddableUser ? 'embeddable' : 'web-responsive-3',
        uspsReferralKey: QS.usps_referral_key || '',
        ...action.payload.marketing,
        ...marketingPayload,
      },
      userTracking: {
        ...trackingPayload,
      },
      context: {
        locale: getLocale(),
        lang: QS.lang,
        next: QS.next || '',
        fixedFeeWaivedUntil: QS.fixed_fee_waived_until,
        ...action.payload.context,
        ...pricePlan,
      },
    };

    const response: RegisterUser.RegisterResponse = yield call(
      authService.register,
      RegisterUser.toJSON(newPayload),
    );

    yield call(Cookies.set, 'jwt', response.data.jwt, { domain: CONFIG.shippoRootUrl });
    yield call(Cookies.set, 'is_logged_in', 'true', { domain: CONFIG.shippoRootUrl });
    const hasAuthenticatedBefore = !!getCookie('copy');
    yield call(Cookies.set, 'copy', response.data.jwt, { domain: CONFIG.shippoRootUrl });

    // Wrapping this part in a try catch so that
    // any error here doesn't create an issue with signup
    try {
      if (!preventMarketoTracking(action.payload)) {
        yield call(marketoSubmit, action.payload.user, marketingPayload);
      }
    } catch (err) {
      // ToDo: Do some logging here
    }

    // If next is not in the url params, we just continue as normal
    // Otherwise, we redirect if the url is absolute, and prepend the Django base url if it's relative
    let nextUrl;
    if (!action.next || (newPayload.context && newPayload.context.plan === 'professional')) {
      nextUrl = '/user/';
    } else {
      const absolute = new RegExp(/^https?:\/\//i);
      if (absolute.test(action.next)) {
        nextUrl = action.next;
      } else {
        nextUrl = CONFIG.djangoBaseUrl + action.next;
      }
    }

    yield put(authActions.registerSuccess(response.data.session_token, nextUrl));
    yield call(Cookies.set, USER_REGISTER_SUCCESS_COOKIE_NAME, 'true', {
      domain: CONFIG.shippoRootUrl,
    });

    if (!embeddableUser)
      yield call(saveInLocalStorage, OnboardingLocalStorageKeys.ONBOARDING_COMPLETED, 'false');

    yield put(authActions.finishRegister());
    // track heap user type event
    const hasPurchasedLabel = getCookie('customer') === 'true';
    const properties = {
      'Visit Type': 'Registration',
      'User Type': hasPurchasedLabel
        ? 'Sketchy Bad Agent?'
        : hasAuthenticatedBefore
          ? 'Return Visitor'
          : 'First Time Visitor',
    };
    yield put(addEventProperties(properties));
    yield put(sendTrackEvent('Account Created'));

    // For the new flow we also need to update onboarding info
    // with the shipping volume from the new signup page
    if (action.payload?.context?.isOnboarding2024) {
      yield put(
        updateOnboardingInfo(
          {
            monthlyVolume: action.payload.context.monthlyShippingVolume || '',
          } as OnboardingInfoFormDataWithValidation,
          response.data.jwt,
        ),
      );
    }
  } catch (err) {
    if (err) {
      const error = err?.response?.data;
      const errorMap = {
        firstName: error?.first_name,
        lastName: error?.last_name,
        email: error?.email,
        password: error?.password,
        errorMessage: error.error || (err.toJSON && err.toJSON()?.message),
      };
      yield put(authActions.registerError(errorMap));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      yield put(stopSubmit('join', errorMap as any));
    }
  }
}

export function* login(action: ReturnType<typeof authActions.login>) {
  try {
    const response: AxiosResponse<{ jwt: string; session_id: string }> = yield call(
      authService.login,
      action.payload,
    );
    yield call(Cookies.set, 'jwt', response.data.jwt, { domain: CONFIG.shippoRootUrl });
    yield call(Cookies.set, 'sessionid', response.data.session_id, {
      domain: CONFIG.shippoRootUrl,
    });
    yield call(Cookies.set, 'is_logged_in', 'true', { domain: CONFIG.shippoRootUrl });
    yield put(authActions.loginSuccess());
    // Find a better way to do this. Using call doesn't work /shrug
    window.location.replace(CONFIG.emberBaseUrl);
  } catch (err) {
    if (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error = (err as any).response.data;
      const errorMap = {
        email: error.email,
        password: error.password,
        errorMessage: error.error,
      };
      yield put(authActions.loginError(errorMap));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      yield put(stopSubmit('login', errorMap as any));
    }
  }
}

export function* resetPassword(action: ReturnType<typeof authActions.resetPassword>) {
  try {
    yield call(authService.resetPassword, action.payload);
    yield put(authActions.resetPasswordSuccess());
  } catch (err) {
    if (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error = (err as any).response.data;
      const errorMap = {
        email: error.email,
        errorMessage: error.error,
      };
      yield put(authActions.resetPasswordError(errorMap));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      yield put(stopSubmit('resetPassword', errorMap as any));
    }
  }
}

export function* resendVerification(action: ReturnType<typeof authActions.resendVerification>) {
  try {
    yield call(authService.resendVerification);
    yield put(authActions.resendVerificationSuccess());
    if (action.payload.withRedirect) {
      yield put(push({ pathname: `/resend_verification/success` }));
    }
  } catch (err) {
    if (err) {
      // Endpoint will always return with a 200 - if there's an error, the
      // endpoint is unavailable for some reason. Returning a generic error
      // reason in that case.
      yield put(
        authActions.resendVerificationError({
          errorMessage: 'Unable to process your request. Please try again later.',
        }),
      );
    }
  }
}

export function* sendRegisterQuestionnare(
  action: ReturnType<typeof authActions.sendRegisterQuestionnaire>,
) {
  try {
    const payload: authService.QuestionnairePayload = {
      company_name: action.payload.companyURL || null,
      phone: action.payload.phoneNumber || null,
      monthly_packages: action.payload.packages ? action.payload.packages.value : null,
      primary_carrier: action.payload.primaryCarrier || null,
      primary_ecommerce_channel: action.payload.primaryECommerce || null,
      is_unsupported_store: !isSupportedStore(action.payload.primaryECommerce || ''),
    };
    const temporaryToken: ReturnType<typeof getTemporaryToken> =
      yield selectState<ReturnType<typeof getTemporaryToken>>(getTemporaryToken);
    const GTMAdditionalInformation: GTMAdditionalInformation = formatGTMAdditionalInformation(
      action.payload.packages ? action.payload.packages.value.toString() : undefined,
      action.payload.primaryCarrier,
    );

    yield call(authService.sendQuestionnaire, payload, temporaryToken);

    yield call(Cookies.set, ADDITIONAL_INFORMATION_GTM_RESULTS, GTMAdditionalInformation, {
      domain: CONFIG.shippoRootUrl,
    });

    yield put(authActions.sendRegisterQuestionnaireSuccess());
    yield put(authActions.finishRegister());
  } catch (err) {
    yield put(authActions.sendRegisterQuestionnaireError());
    // Questionnaire should not block the register flow
    yield put(authActions.finishRegister());
  }
}

/**
 * Parses the QS object and prepares a pricing test object that correspond to register.context payload
 * @param QS query string object parsed from url
 */
export function getStartingPricePlan(QS: { [key: string]: string }) {
  const pricingTags = ['plan', 'tier', 'cycle', 'variant'];
  const pricePlan: Record<string, string> = {};

  pricingTags.forEach((tag) => {
    const qsValue = QS[tag];
    if (qsValue) {
      if (tag === 'variant') {
        pricePlan['pricingTestVariant'] = qsValue;
      } else {
        pricePlan[tag] = qsValue;
      }
    }
  });

  return pricePlan;
}

function withDefaultValue(value, defaultValue) {
  return !value || value === '' ? defaultValue : value;
}

/**
 * Calls the Marketo form submit
 */
export function marketoSubmit(
  user: RegisterUser.User,
  marketingPayload: RegisterUser.MarketingPayload,
) {
  if (!window.MktoForms2) {
    return;
  }

  const mktoForm = window.MktoForms2.getForm(1194);

  if (!mktoForm) {
    return;
  }

  const { email, firstName, lastName } = user;
  const campaign = withDefaultValue(marketingPayload.campaign, 'none');
  const campaignContent = 'shippo_sign_up';
  const medium = withDefaultValue(marketingPayload.medium, 'none');
  const source = withDefaultValue(marketingPayload.source, 'none');
  const campaignTerm = withDefaultValue(marketingPayload.campaignTerm, 'none');

  mktoForm.addHiddenFields({
    // These are the values which will be submitted to Marketo
    Email: email,
    FirstName: firstName,
    LastName: lastName,
    utm_campaign: campaign,
    utm_content: campaignContent,
    utm_medium: medium,
    utm_source: source,
    utm_term: campaignTerm,
    ft_utm_campaign: campaign,
    fTutmcontent: campaignContent,
    ft_utm_medium: medium,
    ft_utm_source: source,
    fTutmterm: campaignTerm,
    eNV: process.env.NODE_ENV,
  });

  return new Promise<void>((resolve) => {
    // the marketo form API doesn't offer an onError callback,
    // since submits should never fail (weirdness by design).
    // so we need to make sure that the promise always resolves,
    // even if there is an error.
    const timeoutId = setTimeout(resolve, 1000);

    mktoForm
      .onSuccess(() => {
        clearTimeout(timeoutId);
        resolve();
      })
      .submit();
  });
}

/**
 * clearRegisterError saga function
 * to clear register error after 2 seconds
 * so that we can register the next error
 */
export function* clearRegisterError() {
  yield delay(2000);
  yield put(authActions.clearAuthError());
}

export default [
  takeLatest(ActionKeys.REGISTER, register),
  takeLatest(ActionKeys.LOGIN, login),
  takeLatest(ActionKeys.RESET_PASSWORD, resetPassword),
  takeLatest(ActionKeys.RESEND_VERIFICATION, resendVerification),
  takeLatest(ActionKeys.SEND_REGISTER_QUESTIONNAIRE, sendRegisterQuestionnare),
  takeLatest(ActionKeys.REGISTER_ERROR, clearRegisterError),
];
